import { isObject } from "@/shared/utils/typeGuards";

export const kUploadUrlsRoute = "/upload-urls";

/**
 * Content types `/upload-urls` will stage. Covers what users upload and what
 * our own capabilities produce: ACE-Step and Restore Audio can render FLAC,
 * and Generate Accompaniment renders the ElevenLabs formats (raw PCM, u-law,
 * A-law, Opus), all of which need to stage so their audio can be fed back.
 */
export const kAllowedContentTypes = [
    "audio/wav",
    "audio/x-wav",
    "audio/mpeg",
    "audio/mp3",
    "audio/aiff",
    "audio/x-aiff",
    "audio/aac",
    "audio/mp4",
    "audio/m4a",
    "audio/x-m4a",
    "audio/flac",
    "audio/x-flac",
    "audio/opus",
    "audio/pcm",
    "audio/ulaw",
    "audio/alaw",
    "application/octet-stream",
];

/**
 * The largest file `/upload-urls` will stage. The client checks a file against
 * this as it is added, so an oversized pick is refused where it was chosen
 * rather than minutes later when a generation tries to upload it.
 */
export const kMaxUploadSize_bytes = 100 * 1024 * 1024;

/** Staging fallback for audio whose container the allowlist doesn't name. */
export const kGenericContentType = "application/octet-stream";

export const isAllowedContentType = (contentType: string): boolean =>
    kAllowedContentTypes.includes(contentType);

export interface UploadUrlFileRequest {
    contentType: string;
    contentLength: number;
    // SHA-256 of the file body, hex-encoded (64 chars). When provided the server
    // uses it as the S3 object key, enabling content-addressed dedup: identical
    // files map to the same key and `uploadUrl` is omitted on the response if the
    // object already exists, so the client can skip the PUT entirely.
    contentHash?: string;
}

export interface UploadUrlsRequestBody {
    files: Record<string, UploadUrlFileRequest>;
}

export interface UploadUrlEntry {
    // Omitted when the request supplied `contentHash` and the object already
    // exists in S3. Callers must skip the PUT in that case.
    uploadUrl?: string;
    key: string;
}

export interface UploadUrlsSuccessResponse {
    ok: true;
    uploads: Record<string, UploadUrlEntry>;
}

// Hex-encoded SHA-256 is exactly 64 lowercase hex characters.
const kSha256HexPattern = /^[0-9a-f]{64}$/;

export const isSha256Hex = (value: unknown): value is string =>
    typeof value === "string" && kSha256HexPattern.test(value);

export const isUploadUrlsRequestBody = (value: unknown): value is UploadUrlsRequestBody =>
    isObject(value) && isObject(value.files);

export const isUploadUrlsSuccessResponse = (
    response: unknown,
): response is UploadUrlsSuccessResponse =>
    isObject(response) && response.ok === true && isObject(response.uploads);
